/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import { isOfflineDraftsEnabled, normalizePeriodKey, META_INVENTORY_KEY, draftCompositeId } from "./countDraftConstants";
import * as countDraftDb from "./countDraftDb";
import { processCountDraftQueue, bindConnectivityHandlers } from "./countDraftSync";
import axiosInstance from "../config/axios";
import logger from "../utils/logger";

function hasLineData(c) {
  if (!c) return false;
  return (
    (c.quantity !== "" && c.quantity !== undefined) ||
    (c.quantity2 !== "" && c.quantity2 !== undefined) ||
    (c.notes && String(c.notes).trim() !== "")
  );
}

export function useCountOfflineDrafts({
  user,
  selectedSiteId,
  countingPeriodStartedAt,
  countingPeriodFetched,
  counts,
  personName,
  generalNotes,
  recalledBatchId,
  inventory,
  isRestoringFromStorageRef
}) {
  const [banner, setBanner] = useState("");
  const [blockSubmit, setBlockSubmit] = useState(false);
  const [lineSyncActionBusy, setLineSyncActionBusy] = useState(false);
  const debounceRef = useRef(null);
  const syncingRef = useRef(false);
  const runSyncRef = useRef(null);
  const [syncUi, setSyncUi] = useState({
    variant: "online_only",
    badgeLabel: "",
    headline: "",
    detail: "",
    showRetryButton: false,
    showRefreshButton: false
  });
  const periodKey =
    countingPeriodFetched && user && user.tenantId && selectedSiteId
      ? normalizePeriodKey(countingPeriodStartedAt)
      : null;
  const enabled =
    isOfflineDraftsEnabled() &&
    user &&
    user.tenantId &&
    user.id &&
    selectedSiteId &&
    periodKey &&
    !recalledBatchId;

  const refreshBanner = useCallback(async () => {
    if (!enabled) {
      setBlockSubmit(false);
      setBanner("");
      const fallback =
        isOfflineDraftsEnabled() && user && user.tenantId
          ? {
              variant: "online_only",
              badgeLabel: "Online",
              headline: "Drafts save when you are online",
              showRetryButton: false,
              showRefreshButton: false,
              detail:
                "Save Draft keeps your work on the server. Submit to your manager from Review when you are ready. My Counts lists only batches you have submitted to your manager."
            }
          : {
              variant: "online_only",
              badgeLabel: "Online",
              headline: "Drafts save when you are online",
              showRetryButton: false,
              showRefreshButton: false,
              detail:
                "Save Draft keeps your work. After you submit to your manager, the batch appears under My Counts."
            };
      setSyncUi(fallback);
      return;
    }
    const st = await countDraftDb.getUnsyncedStateForScreen(user.tenantId, user.id, selectedSiteId, periodKey);
    const offline = typeof navigator !== "undefined" && !navigator.onLine;
    const unsynced = st.pendingDraft || st.anyQueued;
    /** Only pending/syncing lines or queued mutations block Review/Submit — not terminal "error" alone (queue drained). */
    const hasInFlightDraft = st.drafts.some((d) => ["pending", "syncing"].includes(d.syncStatus));
    const hasError = st.drafts.some((d) => d.syncStatus === "error");
    const hasAuthError = st.drafts.some(
      (d) =>
        d.syncStatus === "error" &&
        d.lastError &&
        (/session expired/i.test(String(d.lastError)) || /not authorized/i.test(String(d.lastError)))
    );
    const syncing = syncingRef.current;

    setBlockSubmit(offline || st.anyQueued || hasInFlightDraft);

    let ui;
    if (offline) {
      ui = {
        variant: "offline",
        badgeLabel: "Saved on this device",
        headline: "Saved on this device",
        showRetryButton: false,
        showRefreshButton: false,
        detail:
          "You are offline. Your work is Saved on this device until you reconnect; it uploads when you are back online. Your manager cannot see it until your lines are Synced online and you submit to your manager from Review."
      };
    } else if (hasAuthError) {
      ui = {
        variant: "auth",
        badgeLabel: "Session expired",
        headline: "Session expired",
        showRetryButton: false,
        showRefreshButton: false,
        detail: "Please sign back in so we can finish uploading your count. Your count is still saved on this device."
      };
    } else if (syncing && unsynced) {
      /** Active queue run — show before error so users see "retrying now" instead of a static error while work is in flight. */
      ui = {
        variant: "syncing",
        badgeLabel: hasError ? "Retrying upload" : "Syncing to the server",
        headline: hasError ? "Retrying upload to the server" : "Syncing to the server",
        showRetryButton: false,
        showRefreshButton: false,
        detail: hasError
          ? "Earlier changes did not finish uploading. The app is sending them again now. Stay online and keep this page open if you can. You can keep editing your count."
          : "Your latest entries are uploading. You can keep working."
      };
    } else if (hasError) {
      ui = {
        variant: "error",
        badgeLabel: "Sync issue",
        headline: "Sync issue",
        showRetryButton: true,
        showRefreshButton: true,
        detail:
          "We could not finish uploading. Your count is saved on this device and the app will keep retrying while you are online."
      };
    } else if (unsynced) {
      ui = {
        variant: "waiting",
        badgeLabel: "Waiting to sync",
        headline: "Waiting to sync",
        showRetryButton: true,
        showRefreshButton: true,
        detail:
          "Your changes are queued to upload. The app will try again automatically while you are online. Stay on this screen if you can until you see Synced online. If nothing changes for a long time, tap Retry sync."
      };
    } else {
      ui = {
        variant: "synced",
        badgeLabel: "Synced online",
        headline: "Synced online",
        showRetryButton: false,
        showRefreshButton: false,
        detail:
          "Go to Review when ready, then submit to your manager. My Counts lists only batches you have submitted to your manager."
      };
    }
    setSyncUi(ui);
    setBanner(ui.headline);
  }, [enabled, user, selectedSiteId, periodKey]);

  const runSync = useCallback(
    async (opts) => {
      if (!enabled) return;
      const force = !!(opts && opts.force);
      syncingRef.current = true;
      await refreshBanner();
      try {
        await processCountDraftQueue(
          axiosInstance,
          { tenantId: user.tenantId, userId: user.id, siteId: selectedSiteId, currentPeriodKey: periodKey },
          { force, onProgress: () => {} }
        );
      } finally {
        syncingRef.current = false;
        await refreshBanner();
      }
    },
    [enabled, user, selectedSiteId, periodKey, refreshBanner]
  );

  runSyncRef.current = runSync;

  useEffect(() => {
    refreshBanner();
    const t = setInterval(() => {
      refreshBanner();
      if (typeof navigator !== "undefined" && navigator.onLine) {
        runSync();
      }
    }, 2000);
    return () => clearInterval(t);
  }, [enabled, user, selectedSiteId, periodKey, refreshBanner, runSync]);

  /**
   * Write current on-screen counts + person/notes to IndexedDB and enqueue sync mutations.
   * Does not await server upload — callers decide whether to runSync.
   */
  const writeLocalDraftsToIndexedDb = useCallback(async () => {
    const tenantId = user.tenantId;
    const uid = user.id;
    const site = selectedSiteId;
    const pk = periodKey;
    if (!enabled || !tenantId || !uid || !site || !pk) {
      throw new Error("offline_drafts_unavailable");
    }
    const now = new Date().toISOString();
    try {
      await countDraftDb.migrateLegacyLocalDrafts({
        tenantId,
        userId: uid,
        siteId: site,
        periodKey: pk,
        countingPeriodStartedAt,
        inventoryIds: inventory.map((i) => i.id)
      });
    } catch (e) {
      console.warn("migrateLegacyLocalDrafts", e);
    }
    for (const item of inventory) {
      const c = counts[item.id];
      if (!hasLineData(c)) continue;
      await countDraftDb.removeQueuedMutationsForTarget(tenantId, uid, site, pk, item.id);
      const clientMutationId = crypto.randomUUID();
      await countDraftDb.upsertLocalDraft({
        id: draftCompositeId(tenantId, uid, site, pk, item.id),
        tenantId,
        userId: uid,
        siteId: site,
        currentPeriodKey: pk,
        inventoryItemId: item.id,
        quantity: c.quantity ?? "",
        quantity2: c.quantity2 ?? "",
        notes: c.notes ?? "",
        updatedAt: now,
        syncStatus: "pending",
        lastSyncedAt: null,
        lastError: null,
        serverUpdatedAt: null
      });
      await countDraftDb.enqueueDraftMutation({
        mutationId: clientMutationId,
        tenantId,
        userId: uid,
        siteId: site,
        currentPeriodKey: pk,
        type: "upsert-count-draft",
        payload: {
          clientMutationId,
          siteId: site,
          currentPeriodKey: pk,
          inventoryItemId: item.id,
          quantity: c.quantity,
          quantity2: c.quantity2,
          notes: c.notes,
          personName,
          generalNotes,
          updatedAt: now
        },
        createdAt: now,
        retryCount: 0,
        nextRetryAt: null,
        lastError: null
      });
    }
    if (personName || generalNotes) {
      await countDraftDb.removeQueuedMutationsForTarget(tenantId, uid, site, pk, null);
      const clientMutationId = crypto.randomUUID();
      await countDraftDb.upsertLocalDraft({
        id: draftCompositeId(tenantId, uid, site, pk, META_INVENTORY_KEY),
        tenantId,
        userId: uid,
        siteId: site,
        currentPeriodKey: pk,
        inventoryItemId: META_INVENTORY_KEY,
        quantity: "",
        quantity2: "",
        notes: "",
        personName,
        generalNotes,
        updatedAt: now,
        syncStatus: "pending",
        lastSyncedAt: null,
        lastError: null,
        serverUpdatedAt: null
      });
      await countDraftDb.enqueueDraftMutation({
        mutationId: clientMutationId,
        tenantId,
        userId: uid,
        siteId: site,
        currentPeriodKey: pk,
        type: "upsert-count-draft",
        payload: {
          clientMutationId,
          siteId: site,
          currentPeriodKey: pk,
          inventoryItemId: null,
          quantity: "",
          quantity2: "",
          notes: "",
          personName,
          generalNotes,
          updatedAt: now
        },
        createdAt: now,
        retryCount: 0,
        nextRetryAt: null,
        lastError: null
      });
    }
  }, [
    enabled,
    user,
    selectedSiteId,
    periodKey,
    counts,
    personName,
    generalNotes,
    inventory,
    countingPeriodStartedAt
  ]);

  const persistDraftsToIndexedDb = useCallback(async () => {
    await writeLocalDraftsToIndexedDb();
    await runSync();
    await refreshBanner();
  }, [writeLocalDraftsToIndexedDb, runSync, refreshBanner]);

  /**
   * Shared path for Save Draft and Review Count.
   * Guarantees local IndexedDB persistence before returning localPersisted:true.
   * Server sync is best-effort; awaitSync=false kicks sync without blocking (Review Count).
   */
  const persistCurrentCountDraft = useCallback(
    async ({ reason, awaitSync = true } = {}) => {
      if (!enabled || isRestoringFromStorageRef.current) {
        return { localPersisted: false, skipped: true, reason: reason || null };
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      try {
        await writeLocalDraftsToIndexedDb();
      } catch (e) {
        logger.warn("[offlineDraft] persistCurrentCountDraft local write failed", e && e.message);
        return { localPersisted: false, skipped: false, error: e, reason: reason || null };
      }
      if (awaitSync) {
        try {
          await runSync();
        } catch (e) {
          logger.warn("[offlineDraft] persistCurrentCountDraft sync failed", e && e.message);
        }
        try {
          await refreshBanner();
        } catch (_) {
          /* banner refresh is best-effort */
        }
      } else {
        // Kick sync + banner without blocking navigation (Review Count).
        void (async () => {
          try {
            await runSync();
          } catch (e) {
            logger.warn("[offlineDraft] persistCurrentCountDraft background sync failed", e && e.message);
          }
          try {
            await refreshBanner();
          } catch (_) {
            /* ignore */
          }
        })();
      }
      return { localPersisted: true, skipped: false, reason: reason || null };
    },
    [enabled, isRestoringFromStorageRef, writeLocalDraftsToIndexedDb, runSync, refreshBanner]
  );

  const flushNow = useCallback(async () => {
    if (!enabled || isRestoringFromStorageRef.current) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    try {
      await persistDraftsToIndexedDb();
    } catch (e) {
      logger.warn("[offlineDraft] persist flush failed", e && e.message);
    }
  }, [enabled, isRestoringFromStorageRef, persistDraftsToIndexedDb]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHide = () => {
      void flushNow();
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, [flushNow]);

  const flushDebounced = useCallback(() => {
    if (!enabled || isRestoringFromStorageRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      debounceRef.current = null;
      await persistDraftsToIndexedDb();
    }, 450);
  }, [enabled, isRestoringFromStorageRef, persistDraftsToIndexedDb]);

  useEffect(() => {
    flushDebounced();
  }, [counts, personName, generalNotes, flushDebounced]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    bindConnectivityHandlers(() => {
      if (runSyncRef.current) runSyncRef.current();
    });
    runSync();
  }, [enabled, runSync]);

  const loadDexieSnapshotForInventory = useCallback(
    async (inventoryList) => {
      if (!enabled || !inventoryList || inventoryList.length === 0) return null;
      try {
        const drafts = await countDraftDb.getLocalDraftsForScreen(
          user.tenantId,
          user.id,
          selectedSiteId,
          periodKey
        );
        const merged = {};
        inventoryList.forEach((item) => {
          merged[item.id] = { quantity: "", quantity2: "", notes: "" };
        });
        const byId = new Map(drafts.map((d) => [d.inventoryItemId, d]));
        let hasData = false;
        for (const item of inventoryList) {
          const row = byId.get(item.id);
          if (row && hasLineData({ quantity: row.quantity, quantity2: row.quantity2, notes: row.notes })) {
            merged[item.id] = {
              quantity: row.quantity ?? "",
              quantity2: row.quantity2 ?? "",
              notes: row.notes ?? ""
            };
            hasData = true;
          }
        }
        const meta = drafts.find((d) => d.inventoryItemId === META_INVENTORY_KEY);
        const pn = meta && meta.personName ? meta.personName : "";
        const gn = meta && meta.generalNotes ? meta.generalNotes : "";
        const hasMeta = !!(pn || gn);
        const hasAny = hasData || hasMeta;
        if (hasAny) {
          logger.warn("[offlineDraft] rehydrate source=indexeddb", { lines: hasData, meta: hasMeta });
        }
        return { counts: merged, personName: pn, generalNotes: gn, hasData: hasAny };
      } catch (e) {
        logger.warn("[offlineDraft] rehydrate indexeddb failed", e && e.message);
        return null;
      }
    },
    [enabled, user, selectedSiteId, periodKey]
  );

  const mergeWithLocal = useCallback(
    async (serverDraft, list) => {
      if (!enabled || !serverDraft || !list) return null;
      const srvT = serverDraft.updatedAt ? new Date(serverDraft.updatedAt).getTime() : 0;
      const merged = {};
      list.forEach((item) => {
        merged[item.id] = { quantity: "", quantity2: "", notes: "" };
      });
      const sd = serverDraft.counts || {};
      for (const item of list) {
        const local = await countDraftDb.getLocalDraft(user.tenantId, user.id, selectedSiteId, periodKey, item.id);
        const locT = local && local.updatedAt ? new Date(local.updatedAt).getTime() : 0;
        const useLocal = local && locT >= srvT;
        if (useLocal) {
          merged[item.id] = {
            quantity: local.quantity ?? "",
            quantity2: local.quantity2 ?? "",
            notes: local.notes ?? ""
          };
        } else if (sd[item.id]) {
          const d = sd[item.id];
          merged[item.id] = {
            quantity: d.quantity ?? "",
            quantity2: d.quantity2 ?? "",
            notes: d.notes ?? ""
          };
        } else if (local) {
          merged[item.id] = {
            quantity: local.quantity ?? "",
            quantity2: local.quantity2 ?? "",
            notes: local.notes ?? ""
          };
        }
      }
      const metaLocal = await countDraftDb.getLocalDraft(
        user.tenantId,
        user.id,
        selectedSiteId,
        periodKey,
        META_INVENTORY_KEY
      );
      const metaT = metaLocal && metaLocal.updatedAt ? new Date(metaLocal.updatedAt).getTime() : 0;
      let pn = serverDraft.personName || "";
      let gn = serverDraft.generalNotes || "";
      if (metaLocal && metaT >= srvT) {
        pn = metaLocal.personName || "";
        gn = metaLocal.generalNotes || "";
      }
      return { mergedCounts: merged, personName: pn, generalNotes: gn };
    },
    [enabled, user, selectedSiteId, periodKey]
  );

  const clearScreenStorage = useCallback(async () => {
    if (!enabled) return;
    await countDraftDb.clearScreenDrafts(user.tenantId, user.id, selectedSiteId, periodKey);
  }, [enabled, user, selectedSiteId, periodKey]);

  /** Reset terminal IndexedDB line errors, re-queue from current screen state, force a fresh sync (Retry sync / after bulk Save Draft). */
  const reconcileLineSyncFromScreen = useCallback(async () => {
    if (!enabled) return;
    setLineSyncActionBusy(true);
    try {
      const resetN = await countDraftDb.resetErrorDraftsToPending(user.tenantId, user.id, selectedSiteId, periodKey);
      if (resetN > 0) {
        logger.warn("[offlineDraft] reset error draft rows to pending", { count: resetN });
      }
      await persistDraftsToIndexedDb();
      // Force clears a stale in-flight lock left by a hung/timeout request so Retry sync always starts fresh.
      await runSync({ force: true });
      await refreshBanner();
    } catch (e) {
      logger.warn("[offlineDraft] reconcileLineSyncFromScreen failed", e && e.message);
    } finally {
      setLineSyncActionBusy(false);
    }
  }, [enabled, user, selectedSiteId, periodKey, persistDraftsToIndexedDb, runSync, refreshBanner]);

  return {
    banner,
    blockSubmit,
    syncUi,
    mergeWithLocal,
    clearScreenStorage,
    loadDexieSnapshotForInventory,
    flushOfflineDraftsNow: flushNow,
    persistCurrentCountDraft: enabled ? persistCurrentCountDraft : null,
    retryLineSync: enabled ? reconcileLineSyncFromScreen : null,
    afterBulkDraftSavedOnline: enabled ? reconcileLineSyncFromScreen : null,
    lineSyncActionBusy,
    periodKey,
    enabled
  };
}

